import { History } from "history";
import { renderRoutes } from "react-router-config";

import { defineApi } from "@tsp/api";
import { RuntimeEnvironments } from "@tsp/env";
import { initSentry } from "@tsp/sentry";
import { combineEpics } from "@tsp/utils/epics";

import { lockerSlice } from "@tsp-core/locker/slice";
import { notifierSlice } from "@tsp-core/notifier/slice";

import { accountsApi } from "@tsp-domains/accounts/api";
import { accrualModelsApi } from "@tsp-domains/accrual-models/api";
import { companiesApi } from "@tsp-domains/companies/api";
import { contractTypesApi } from "@tsp-domains/contract-types/api";
import { contractorsAccountsApi } from "@tsp-domains/contractor-accounts/api";
import { contractsApi } from "@tsp-domains/contracts/api";
import { countriesApi } from "@tsp-domains/countries/api";
import { dashboardApi } from "@tsp-domains/dashboard/api";
import { departmentsApi } from "@tsp-domains/departments/api";
import { externalResourcesEpic } from "@tsp-domains/external-resources/epic";
import { externalResourcesReducer } from "@tsp-domains/external-resources/reducer";
import { generalLedgerAccountsApi } from "@tsp-domains/general-ledger-accounts/api";
import { invoicesApi } from "@tsp-domains/invoices/api";
import { notificationsApi } from "@tsp-domains/notifications/api";
import { overviewApi } from "@tsp-domains/overview/api";
import { partiesApi } from "@tsp-domains/parties/api";
import { paymentSystemApi } from "@tsp-domains/payment-system/api";
import { positionsApi } from "@tsp-domains/positions/api";
import { projectsPortfolioApi } from "@tsp-domains/projects-portfolio/api";
import { soulEntitiesApi } from "@tsp-domains/soul-entities/api";
import { supplierWorkTypesEpic } from "@tsp-domains/supplier-work-types/epic";
import { supplierWorkTypesReducer } from "@tsp-domains/supplier-work-types/reducer";
import { systemTypesApi } from "@tsp-domains/system-types/api";
import { workItemsApi } from "@tsp-domains/work-items/api";
import { ownWorkTypeAndSubTypeApi, workSubTypesApi, workTypesApi } from "@tsp-domains/work-types/api";

import apiBlank from "./api";
import { configReducer } from "./features/config";
import { createEnvReducer } from "./features/env";
import { externalEpic } from "./features/external";
import { payoutEpic } from "./features/payout";
import { userEpic, userReducer } from "./features/user";
import { EpicDependencies, State } from "./interface";
import { routes } from "./routes";
import { createApplication } from "./store";

export interface WrapperFactoryProps {
  history: History;
  env: RuntimeEnvironments;
}

const rootEpic = combineEpics(userEpic, payoutEpic, externalEpic, supplierWorkTypesEpic, externalResourcesEpic);

export const createRootComponent = (props: WrapperFactoryProps) => {
  const { history, env } = props;

  const reducers = {
    user: userReducer,
    config: configReducer,
    externalResources: externalResourcesReducer,
    env: createEnvReducer(env),
    supplierWorkTypes: supplierWorkTypesReducer,

    [lockerSlice.reducerPath]: lockerSlice.reducer,
    [notifierSlice.reducerPath]: notifierSlice.reducer,

    [accountsApi.reducerPath]: accountsApi.reducer,
    [accrualModelsApi.reducerPath]: accrualModelsApi.reducer,
    [companiesApi.reducerPath]: companiesApi.reducer,
    [contractTypesApi.reducerPath]: contractTypesApi.reducer,
    [contractorsAccountsApi.reducerPath]: contractorsAccountsApi.reducer,
    [contractsApi.reducerPath]: contractsApi.reducer,
    [countriesApi.reducerPath]: countriesApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [departmentsApi.reducerPath]: departmentsApi.reducer,
    [generalLedgerAccountsApi.reducerPath]: generalLedgerAccountsApi.reducer,
    [invoicesApi.reducerPath]: invoicesApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [overviewApi.reducerPath]: overviewApi.reducer,
    [ownWorkTypeAndSubTypeApi.reducerPath]: ownWorkTypeAndSubTypeApi.reducer,
    [partiesApi.reducerPath]: partiesApi.reducer,
    [paymentSystemApi.reducerPath]: paymentSystemApi.reducer,
    [positionsApi.reducerPath]: positionsApi.reducer,
    [projectsPortfolioApi.reducerPath]: projectsPortfolioApi.reducer,
    [soulEntitiesApi.reducerPath]: soulEntitiesApi.reducer,
    [systemTypesApi.reducerPath]: systemTypesApi.reducer,
    [workItemsApi.reducerPath]: workItemsApi.reducer,
    [workSubTypesApi.reducerPath]: workSubTypesApi.reducer,
    [workTypesApi.reducerPath]: workTypesApi.reducer,
  };

  const api = defineApi(apiBlank, { origin: env.DOCFLOW_SERVER_ORIGIN });

  const dependencies = {
    api,
    initSentry,
  };

  return createApplication<State, EpicDependencies>({
    history,
    epicConfig: {
      rootEpic,
      dependencies,
    },
    storeConfig: {
      reducers,
      middlewares: [],
    },
  });
};

export default function App() {
  return <>{renderRoutes(routes)}</>;
}
